import { useCallback, useEffect, useMemo, useState } from "react";
import { includes, some, without } from "lodash-es";
import { SearchState } from "react-instantsearch-core";
import { HitType, MultiselectHitType } from "src/types/Algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  createCategoryTree,
  getItemsToRefine,
} from "../utils/createCategoryTree";
import { CATEGORIES_FACET } from "src/utils/algolia";
import { getSelectedNodes } from "../utils/getSelectedNodes";

interface MultiselectReturn {
  tree?: MultiselectHitType[];
  handleRefine: (hit: MultiselectHitType) => void;
  isSelected: (hit: MultiselectHitType) => boolean;
}

export const useMultiselect = ({
  items,
  genderId,
  currentRefinement,
  setStatePayload,
  departmentSlug,
  isNewPlp,
  isMultiselectCategories,
  slug,
  currentSlug,
}: {
  items: HitType[];
  genderId: string;
  currentRefinement: string[];
  setStatePayload?: (
    payload: SearchState & { selectedCatSlug: string[] }
  ) => void;
  departmentSlug?: string;
  isNewPlp: boolean;
  categoriesTree?: MultiselectHitType[];
  isMultiselectCategories: boolean;
  slug?: string | string[];
  currentSlug?: string;
}): MultiselectReturn => {
  const [cachedItems, setCachedItems] = useState<HitType[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [syncWithState, setSyncWithState] = useState(false);
  const gender = localeFix({ gender: genderId });

  const handleRefine = useCallback(
    (hit: MultiselectHitType) => {
      if (includes(selectedIds, hit.label)) {
        setSelectedIds(without(selectedIds, hit.label));
      } else {
        setSelectedIds([...selectedIds, hit.label]);
      }
      setSyncWithState(true);
    },
    [selectedIds]
  );

  const tree = useMemo(() => {
    const data = createCategoryTree(cachedItems, gender).tree[0]?.items;

    if (isMultiselectCategories) {
      return data;
    }

    const isNewInRoot = !slug && isNewPlp;
    if (!currentSlug || (departmentSlug && !currentSlug) || isNewInRoot) {
      return data;
    }
    if (currentSlug === genderId) {
      return data;
    }
    const hasSelected = some(
      data,
      (item) => item.label?.split("||")[2] === currentSlug
    );
    if (hasSelected) {
      return data;
    } else {
      return getSelectedNodes(data, currentSlug);
    }
  }, [gender, cachedItems, currentSlug]);

  const isSelected = (hit: HitType) => includes(selectedIds, hit.label);

  useEffect(() => {
    if (JSON.stringify(items) !== JSON.stringify(cachedItems)) {
      setCachedItems(items);
    }
  }, [items]);

  useEffect(() => {
    if (!syncWithState) {
      return;
    }
    const toRefine = getItemsToRefine(selectedIds);
    if (setStatePayload) {
      setStatePayload({
        refinementList: { [CATEGORIES_FACET]: toRefine },
        selectedCatSlug: selectedIds,
      });
    }
  }, [selectedIds]);

  useEffect(() => {
    setSyncWithState(false);
    if (JSON.stringify(currentRefinement) !== JSON.stringify(selectedIds)) {
      setSelectedIds(currentRefinement);
    }
  }, [currentRefinement]);

  return {
    tree,
    handleRefine,
    isSelected,
  };
};
